/**
 * Site Settings V2 — "Alarms" section: the Quiet Hours (ignore
 * window) controls plus the per-(site, alarm) enable / notify /
 * ignore-during-window toggle tree.
 *
 * Contains:
 * - AlarmsSection (default export)
 */

import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { Chip } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AlarmTree, groupAlarmsByParent } from '@sophocles/components/AlarmTree';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTimePickerField,
} from '@sophocles/views/settings/components';

// Site-settings alarm section: combines the Quiet Hours / Ignore
// Window controls with the per-(site, alarm) toggle tree under a
// single top-level "Alarms" surface.  Quiet Hours toggles
// notification suppression during a recurring time-of-day window;
// the Alarm Categories tree controls per-alarm enable / notify /
// ignore-during-window.  Same `<AlarmTree>` the user-settings
// alarm-subscriptions panel uses.
export default function AlarmsSection({ disabled }) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const { values } = useFormikContext();
  const alarms = values.alarms || [];
  // Ignore-window column is only meaningful when Quiet Hours is on
  // (per the subsection above).  Off → drop the column so the row
  // layout doesn't render an always-disabled control.
  const showIgnoredColumn = Boolean(values.ignore_window?.enabled);
  const channels = useMemo(() => {
    const base = [
      {
        key: 'enabled',
        label: t('siteSettings.alarmsEnabled', 'Enabled'),
        tooltip: t(
          'siteSettings.alarmsEnabledTooltip',
          'When on, this alarm fires for the site. Off — the alarm is suppressed entirely.',
        ),
      },
      {
        key: 'alert_users_on_alarm',
        label: t('siteSettings.alarmsNotify', 'Notify'),
        tooltip: t(
          'siteSettings.alarmsNotifyTooltip',
          'When on, subscribed users receive notifications (email / SMS) when this alarm fires.',
        ),
      },
    ];
    if (showIgnoredColumn) {
      base.push({
        key: 'ignored',
        label: t('siteSettings.alarmsIgnored', 'Ignored'),
        tooltip: t(
          'siteSettings.alarmsIgnoredTooltip',
          'When on, this alarm is muted during the configured Ignore Window. Outside the window it fires normally.',
        ),
      });
    }
    return base;
  }, [t, showIgnoredColumn]);

  const { groups, orphans } = useMemo(
    () =>
      groupAlarmsByParent(alarms, row => {
        if (row.parent_id == null) return null;
        const name = row.parent_key
          ? tAlarm(`alarms.cat_${row.parent_key}`, { defaultValue: row.parent_name })
          : row.parent_name;
        const description = row.parent_key
          ? tAlarm(`alarms.cat_${row.parent_key}_description`, { defaultValue: row.parent_description })
          : row.parent_description;
        return {
          id: row.parent_id,
          name,
          description,
          sortKey: name?.toLowerCase(),
        };
      }),
    [alarms, tAlarm],
  );

  return (
    <SettingsSection
      id="settings-alarms"
      icon={<NotificationsActiveIcon />}
      title={t('siteSettings.v2.alarms', 'Alarms')}
      description={t(
        'siteSettings.v2.alarmsDescription',
        'Quiet hours for the whole site plus per-alarm enable / notify / ignore overrides. Grouped by category — toggle a group to fan-out to every alarm under it.',
      )}
    >
      <SettingsSubsection
        title={t('siteSettings.v2.quietHours', 'Quiet Hours (Ignore Window)')}
        description={t(
          'siteSettings.v2.quietHoursDescription',
          'Mute alarm notifications during a recurring time-of-day window. Per-alarm rows below opt each alarm in via the Ignored column once Quiet Hours is on.',
        )}
        bordered
      >
        <SettingsFieldRow
          label={t('siteSettings.v2.quietHoursEnabled', 'Quiet Hours Enabled')}
          description={t(
            'siteSettings.v2.quietHoursEnabledDescription',
            'When on, alarms marked Ignored below are suppressed during the configured window.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="ignore_window.enabled" hideLabel />
        </SettingsFieldRow>
        {values.ignore_window?.enabled ? (
          <>
            <SettingsFieldRow
              label={t('siteSettings.ignoreWindowStart', 'Start Time')}
              description={t('siteSettings.v2.startTimeDescription', 'When suppression begins.')}
              controlWidth={180}
            >
              <SettingsTimePickerField name="ignore_window.start_time" views={['hours', 'minutes']} hiddenLabel />
            </SettingsFieldRow>
            <SettingsFieldRow
              label={t('siteSettings.ignoreWindowEnd', 'End Time')}
              description={t(
                'siteSettings.v2.endTimeDescription',
                'When suppression ends. Wraps past midnight if earlier than start.',
              )}
              controlWidth={180}
            >
              <SettingsTimePickerField name="ignore_window.end_time" views={['hours', 'minutes']} hiddenLabel />
            </SettingsFieldRow>
          </>
        ) : null}
      </SettingsSubsection>

      <SettingsSubsection title={t('siteSettings.v2.alarmCategories', 'Alarm Categories')} bordered flush>
        <AlarmTree
          alarms={alarms}
          groups={groups}
          orphans={orphans}
          channels={channels}
          disabled={disabled}
          emptyText={t('siteSettings.noAlarms', 'No alarms configured for this site.')}
          rowKey={row => row.alarm_id}
          rowTitle={row =>
            row.key ? tAlarm(`alarms.${row.key}`, { defaultValue: row.name }) : row.name || `#${row.alarm_id}`
          }
          rowSubtitle={row => {
            const rowName = row.key ? tAlarm(`alarms.${row.key}`, { defaultValue: row.name }) : row.name;
            const categoryName = row.category_key
              ? tAlarm(`alarms.cat_${row.category_key}`, { defaultValue: row.category_name })
              : row.category_name;
            const categoryDescription = row.category_key
              ? tAlarm(`alarms.cat_${row.category_key}_description`, { defaultValue: row.category_description })
              : row.category_description;
            const parentName = row.parent_key
              ? tAlarm(`alarms.cat_${row.parent_key}`, { defaultValue: row.parent_name })
              : row.parent_name;
            // Suppress fallbacks that just repeat the row title — leaf
            // categories with empty descriptions or descriptions that are
            // basically the alarm's own name add no information.
            if (categoryDescription && categoryDescription !== rowName) return categoryDescription;
            if (categoryName && categoryName !== rowName && categoryName !== (parentName ?? '')) return categoryName;
            return null;
          }}
          rowChips={row =>
            row.clearable ? (
              <Chip
                size="small"
                variant="outlined"
                color="default"
                label={t('siteSettings.alarmsClearable', 'Manual')}
                sx={{ height: 18 }}
              />
            ) : null
          }
        />
      </SettingsSubsection>
    </SettingsSection>
  );
}
